import mapJson from 'jsonpath-mapper';
import { MigrateRequest } from '~/services';
import { UUID } from '~/util/guid';
import { progress } from '~/util/logger';

const mapComponentsAndContentTypes = (body: any): MigrateRequest =>
  mapJson(body, {
    components: 'components',
    contentTypes: ['contentTypes', 'contenttypes', 'content-types'],
  });

const validateArrayQueryValues = (ret: any[]) => {
  if (ret && !Array.isArray(ret)) return ['disallowed'];
  if (ret?.every((v) => ['', null, undefined].includes(v.trim())))
    return ['disallowed'];
  return ret;
};

export const mapBodyToPayload = (body: any) => {
  const payload: MigrateRequest = {
    ...mapJson(body, {
      source: 'source',
      target: ['target', 'source'],
      callback: {
        id: { $path: 'callback.id', $default: () => UUID.generate() },
        uri: { $path: 'callback.uri', $disable: (uri) => !uri },
      },
      concurrency: 'concurrency',
      entries: 'entries',
      nodes: 'nodes',
      tagGroups: 'tagGroups',
      tags: 'tags',
      readOnlyFields: { $path: 'readOnlyFields', $default: () => false },
      transformContentTypes: 'transformContentTypes',
      transformGuids: {
        $path: 'transformGuids',
        $formatting: (flag) => !(flag === 'false' || flag === false),
        $default: () => true,
      },
      partialEntries: {
        $path: 'partialEntries',
        $formatting: (flag) => !(flag === 'false' || flag === false),
        $default: () => false,
      },
      models: 'models',
      migrate: ({ migrate }) => migrate || mapComponentsAndContentTypes(body),
      copyField: 'copyField',
      updateField: 'updateField',
      delete: () => mapComponentsAndContentTypes(body.delete || body),
      duplicate: ({ duplicate }) =>
        duplicate || mapComponentsAndContentTypes(body),
      // map each query parameter individually to strip unknown query options
      query: {
        $path: 'query',
        $formatting: {
          assetTypes: {
            $path: 'assetTypes',
            $return: validateArrayQueryValues,
          },
          contentTypeIds: {
            $path: 'contentTypeIds',
            $return: validateArrayQueryValues,
          },
          dataFormats: {
            $path: 'dataFormats',
            $return: validateArrayQueryValues,
          },
          entryTitles: {
            $path: 'entryTitles',
            $return: validateArrayQueryValues,
          },
          excludeIds: {
            $path: 'excludeIds',
            $return: validateArrayQueryValues,
          },
          fields: 'fields',
          filters: 'filters',
          includeIds: {
            $path: 'includeIds',
            $return: validateArrayQueryValues,
          },
          includePaths: {
            $path: 'includePaths',
            $return: validateArrayQueryValues,
          },
          languages: 'languages',
          pageSize: 'pageSize',
          searchTerm: 'searchTerm',
          useDelivery: 'useDelivery',
          versionStatus: 'versionStatus',
          webpageTemplates: {
            $path: 'webpageTemplates',
            $return: validateArrayQueryValues,
          },
          where: {
            $path: 'where',
            $return: validateArrayQueryValues,
          },
        },
        $return: (obj: any) => {
          if (!obj) return obj;
          const mappedQuery = Object.fromEntries(
            Object.entries(obj).filter(([, v]) => typeof v !== 'undefined')
          );
          if (!mappedQuery || Object.keys(mappedQuery).length === 0)
            return { includeIds: ['disallowed'] };
          return mappedQuery;
        },
      },
      zenQL: ['zenQL', 'zenql', 'zenQl'],
      ignoreErrors: 'ignoreErrors',
      noCache: 'noCache',
      includeDefaults: { $path: 'includeDefaults', $default: () => true },
      includeValidations: { $path: 'includeValidations', $default: () => true },
      stopLevel: 'stopLevel',
      outputLogs: { $path: 'outputLogs', $default: () => false },
      outputProgress: { $path: 'outputProgress', $default: () => false },
    }),
    // This bit allows us to map any operation to content types and components array
    // supplied in the root of the payload however this might be dangerous
    // if the url is the only safety mechanism for something like an accidental
    // delete of content types
    ...mapComponentsAndContentTypes(body),
  };

  progress.active = !!(payload.outputProgress && process.stdout.isTTY);

  return payload;
};
